import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { ApiTimeout, useBackendApi } from '@/hooks/fetch';
import type { FcAdminApiResponse, FcAdminListData, FcAdminOperation, FcAdminQueryRequest, FcAdminStatusData } from '@shared/fcAdminApiTypes';
import { formatFcAdminNotice } from '@/lib/fcAdminErrors';
import { deriveFcAdminAccess, fcAdminAccessFingerprint, isDefinitiveFcAdminAccessDenial, isFcAdminDashboardResponseCurrent, planFcAdminStatusResult, selectFcAdminReadStatus, type FcAdminDashboardRequestGuard } from '@/lib/fcAdminAccess';

export type FcAdminDashboardSummary = {
    online?: number;
    maxPlayers?: number;
    team?: number;
    uptime?: number;
    bansToday?: number;
    warnsToday?: number;
};

type FcAdminConnectionContextValue = {
    status?: FcAdminStatusData;
    error?: string;
    errorCode?: string;
    loading: boolean;
    refresh: () => void;
};

type FcAdminAccessContextValue = {
    status?: FcAdminStatusData;
    authorized: boolean;
    mutationReady: boolean;
    capabilities: ReadonlySet<FcAdminOperation>;
    can: (operation: FcAdminOperation) => boolean;
    accessRevision: string;
};

type FcAdminSummaryContextValue = {
    summary?: FcAdminDashboardSummary;
};

const maxTransientStatusRetries = 12;

const FcAdminConnectionContext = createContext<FcAdminConnectionContextValue | null>(null);
const FcAdminAccessContext = createContext<FcAdminAccessContextValue | null>(null);
const FcAdminSummaryContext = createContext<FcAdminSummaryContextValue | null>(null);

export function FcAdminStatusProvider({ children }: { children: React.ReactNode }) {
    const [location] = useLocation();
    const [status, setStatus] = useState<FcAdminStatusData>();
    const [accessStatus, setAccessStatus] = useState<FcAdminStatusData>();
    const [summary, setSummary] = useState<FcAdminDashboardSummary>();
    const [error, setError] = useState<string>();
    const [errorCode, setErrorCode] = useState<string>();
    const [loading, setLoading] = useState(true);
    const mounted = useRef(true);
    const statusInFlight = useRef(false);
    const dashboardInFlight = useRef<number>();
    const dashboardEpoch = useRef(0);
    const dashboardScope = useRef<string>();
    const dashboardGuard = useRef<FcAdminDashboardRequestGuard>({ epoch: 0, route: '', accessRevision: 'unavailable', allowed: false });
    const authorizationFingerprint = useRef<string>();
    const accessFingerprint = useRef<string>();
    const serverAccessRevision = useRef<string>();
    const accessStatusRef = useRef<FcAdminStatusData>();
    const capabilityRevision = useRef<string>();
    const stableCapabilities = useRef<ReadonlySet<FcAdminOperation>>(new Set());
    const statusRef = useRef<FcAdminStatusData>();
    const transientRetryTimer = useRef<number>();
    const transientRetryAttempts = useRef(0);
    const transientSince = useRef<number>();
    const refreshStatusRef = useRef<(background?: boolean) => Promise<void>>();
    const statusApi = useBackendApi<FcAdminApiResponse<FcAdminStatusData>>({ method: 'GET', path: '/fc/status', abortOnUnmount: true });
    const statusApiRef = useRef(statusApi);
    statusApiRef.current = statusApi;
    const dashboardApi = useBackendApi<FcAdminApiResponse<FcAdminListData>, FcAdminQueryRequest>({ method: 'POST', path: '/fc/query/dashboard.read', abortOnUnmount: true });
    const dashboardApiRef = useRef(dashboardApi);
    dashboardApiRef.current = dashboardApi;
    const authorized = accessStatus?.discordLinked === true && accessStatus.actorAuthorized === true;
    const accessRevision = accessStatus?.accessRevision ?? 'unavailable';
    const dashboardAllowed = authorized && accessStatus?.capabilities.some((entry) => entry.operation === 'dashboard.read' && entry.allowed) === true;
    const nextDashboardScope = `${location}|${accessRevision}|${dashboardAllowed ? '1' : '0'}`;
    if (dashboardScope.current !== nextDashboardScope) {
        dashboardScope.current = nextDashboardScope;
        dashboardEpoch.current += 1;
    }
    dashboardGuard.current = { epoch: dashboardEpoch.current, route: location, accessRevision, allowed: dashboardAllowed };

    const clearAccess = useCallback(() => {
        if (!mounted.current) return;
        dashboardEpoch.current += 1;
        dashboardScope.current = undefined;
        dashboardGuard.current = { ...dashboardGuard.current, epoch: dashboardEpoch.current, accessRevision: 'unavailable', allowed: false };
        authorizationFingerprint.current = undefined;
        accessFingerprint.current = undefined;
        serverAccessRevision.current = undefined;
        capabilityRevision.current = undefined;
        stableCapabilities.current = new Set();
        accessStatusRef.current = undefined;
        setAccessStatus(undefined);
        setSummary(undefined);
    }, []);

    const degradeStatus = useCallback((message: string, code: string) => {
        if (!mounted.current) return;
        setError(message);
        setErrorCode(code);
    }, []);

    const scheduleTransientRetry = useCallback((retryAfterMs?: number) => {
        if (!mounted.current || transientRetryTimer.current !== undefined || transientRetryAttempts.current >= maxTransientStatusRetries) return;
        transientRetryAttempts.current += 1;
        const requested = typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs) ? retryAfterMs : 3000;
        transientRetryTimer.current = window.setTimeout(() => {
            transientRetryTimer.current = undefined;
            void refreshStatusRef.current?.(true);
        }, Math.min(Math.max(requested, 1000), 15000));
    }, []);

    const refreshStatus = useCallback(async (background = false) => {
        if (statusInFlight.current || document.hidden) return;
        statusInFlight.current = true;
        if (!background && mounted.current) setLoading(true);
        let requestError: string | undefined;
        try {
            const response = await statusApiRef.current({ timeout: ApiTimeout.LONG, error: (message) => { requestError = message; } });
            if (!mounted.current) return;
            if (!response) {
                degradeStatus('Der FC-Status ist vorübergehend nicht erreichbar. Der letzte bestätigte Lesezugriff bleibt sichtbar; Änderungen sind gesperrt.', requestError ? 'network_error' : 'empty_response');
                return;
            }
            const now = Date.now();
            const plan = planFcAdminStatusResult(statusRef.current, response, transientSince.current, now);
            if (plan.transientCode) {
                if (transientSince.current === undefined) transientSince.current = now;
                scheduleTransientRetry(plan.retryAfterMs);
            }
            if (plan.action === 'keep' && plan.status) {
                statusRef.current = plan.status;
                setStatus(plan.status);
                setError(undefined);
                setErrorCode(undefined);
                return;
            }
            if (!response.ok) {
                degradeStatus(formatFcAdminNotice(response), response.code);
                return;
            }
            if (!plan.transientCode) {
                transientRetryAttempts.current = 0;
                transientSince.current = undefined;
            }
            const nextAccessStatus = selectFcAdminReadStatus(accessStatusRef.current, response.data);
            if (!nextAccessStatus && isDefinitiveFcAdminAccessDenial(response.data)) clearAccess();
            const nextFingerprint = fcAdminAccessFingerprint(response.data);
            const nextAccessFingerprint = [
                nextFingerprint,
                response.data.accessRevision,
                response.data.bridgeConfigured ? '1' : '0',
                response.data.gameServerOnline ? '1' : '0',
                response.data.resourceOnline ? '1' : '0',
                response.data.offlineSnapshot?.capturedAt ?? '',
                response.data.actor?.inGameConnected ? '1' : '0',
                JSON.stringify(response.data.groupOptions ?? []),
            ].join('|');
            if (nextAccessStatus === response.data && (authorizationFingerprint.current !== nextFingerprint || serverAccessRevision.current !== response.data.accessRevision)) {
                dashboardEpoch.current += 1;
                dashboardScope.current = undefined;
                dashboardGuard.current = { ...dashboardGuard.current, epoch: dashboardEpoch.current, accessRevision: response.data.accessRevision, allowed: false };
                setSummary(undefined);
            }
            if (nextAccessStatus === response.data) {
                authorizationFingerprint.current = nextFingerprint;
                serverAccessRevision.current = response.data.accessRevision;
                if (accessFingerprint.current !== nextAccessFingerprint) {
                    accessFingerprint.current = nextAccessFingerprint;
                    accessStatusRef.current = response.data;
                    setAccessStatus(response.data);
                }
            }
            statusRef.current = response.data;
            setStatus(response.data);
            setError(undefined);
            setErrorCode(undefined);
        } catch {
            degradeStatus('Der FC-Status ist vorübergehend nicht erreichbar. Der letzte bestätigte Lesezugriff bleibt sichtbar; Änderungen sind gesperrt.', 'network_error');
        } finally {
            statusInFlight.current = false;
            if (mounted.current && !background) setLoading(false);
        }
    }, [clearAccess, degradeStatus, scheduleTransientRetry]);
    refreshStatusRef.current = refreshStatus;

    const refreshDashboard = useCallback(async () => {
        const requestGuard = dashboardGuard.current;
        if (dashboardInFlight.current === requestGuard.epoch || document.hidden || !requestGuard.allowed || requestGuard.route !== '/') return;
        dashboardInFlight.current = requestGuard.epoch;
        try {
            const response = await dashboardApiRef.current({ data: { limit: 1 }, timeout: ApiTimeout.LONG });
            if (mounted.current && response?.ok && isFcAdminDashboardResponseCurrent(requestGuard, dashboardGuard.current)) {
                setSummary(response.data.items[0] as FcAdminDashboardSummary | undefined);
            }
        } finally {
            if (dashboardInFlight.current === requestGuard.epoch) dashboardInFlight.current = undefined;
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        void refreshStatus(false);
        const tick = () => { if (!document.hidden) void refreshStatus(true); };
        const interval = window.setInterval(tick, 30000);
        const visible = () => { if (!document.hidden) void refreshStatus(false); };
        document.addEventListener('visibilitychange', visible);
        return () => {
            mounted.current = false;
            window.clearInterval(interval);
            window.clearTimeout(transientRetryTimer.current);
            transientRetryTimer.current = undefined;
            document.removeEventListener('visibilitychange', visible);
        };
    }, [refreshStatus]);

    useEffect(() => {
        if (location !== '/' || !dashboardAllowed) {
            setSummary(undefined);
            return undefined;
        }
        void refreshDashboard();
        const tick = () => { if (!document.hidden) void refreshDashboard(); };
        const interval = window.setInterval(tick, 60000);
        const visible = () => { if (!document.hidden) void refreshDashboard(); };
        document.addEventListener('visibilitychange', visible);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', visible);
        };
    }, [accessRevision, dashboardAllowed, location, refreshDashboard]);

    const access = useMemo(() => {
        const readAccess = deriveFcAdminAccess(accessStatus);
        const currentAccess = deriveFcAdminAccess(status);
        const allowed = readAccess.authorized ? accessStatus?.capabilities.filter((entry) => entry.allowed).map((entry) => entry.operation).sort() ?? [] : [];
        const revision = accessStatus?.accessRevision ?? 'unavailable';
        if (capabilityRevision.current !== revision) {
            capabilityRevision.current = revision;
            stableCapabilities.current = new Set(allowed);
        }
        return {
            authorized: readAccess.authorized,
            mutationReady: readAccess.authorized && !error && currentAccess.mutationReady,
            capabilities: stableCapabilities.current,
        };
    }, [accessStatus, error, status]);
    const capabilitiesRef = useRef(access.capabilities);
    capabilitiesRef.current = access.capabilities;
    const can = useCallback((operation: FcAdminOperation) => capabilitiesRef.current.has(operation), []);
    const refresh = useCallback(() => { void refreshStatus(false); }, [refreshStatus]);
    const connectionValue = useMemo(() => ({ status, error, errorCode, loading, refresh }), [status, error, errorCode, loading, refresh]);
    const accessValue = useMemo(() => ({ status: accessStatus, authorized: access.authorized, mutationReady: access.mutationReady, capabilities: access.capabilities, can, accessRevision }), [accessStatus, access, can, accessRevision]);
    const summaryValue = useMemo(() => ({ summary }), [summary]);
    return <FcAdminConnectionContext.Provider value={connectionValue}><FcAdminAccessContext.Provider value={accessValue}><FcAdminSummaryContext.Provider value={summaryValue}>{children}</FcAdminSummaryContext.Provider></FcAdminAccessContext.Provider></FcAdminConnectionContext.Provider>;
}

export const useFcAdminConnectionContext = () => {
    const value = useContext(FcAdminConnectionContext);
    if (!value) throw new Error('FcAdminStatusProvider fehlt.');
    return value;
};

export const useFcAdminAccessContext = () => {
    const value = useContext(FcAdminAccessContext);
    if (!value) throw new Error('FcAdminStatusProvider fehlt.');
    return value;
};

export const useFcAdminSummaryContext = () => {
    const value = useContext(FcAdminSummaryContext);
    if (!value) throw new Error('FcAdminStatusProvider fehlt.');
    return value;
};

export const useFcAdminStatusContext = () => {
    const connection = useFcAdminConnectionContext();
    const access = useFcAdminAccessContext();
    const summary = useFcAdminSummaryContext();
    return useMemo(() => ({ ...access, ...connection, ...summary }), [access, connection, summary]);
};
